import React from "react";
import alogo from "../../Assets/Logos/ashok1.png";
import digilocker from "../../Assets/Logos/digilocker.png";
import check from "../../Assets/Logos/check.png";
import { QRCodeSVG } from "qrcode.react";

// ✅ safeString: converts ANY value (object, array, null, undefined) to a safe renderable string
const safeString = (value, fieldName = "unknown") => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  if (value instanceof Date) return value.toLocaleString();
  if (Array.isArray(value)) {
    console.warn(
      `⚠️ [DigiLockerRC] Array field "${fieldName}":`,
      JSON.stringify(value),
    );
    return (
      value
        .map((v) => safeString(v))
        .filter(Boolean)
        .join(", ") || ""
    );
  }
  if (typeof value === "object") {
    console.error(
      `❌ [DigiLockerRC] Object field "${fieldName}":`,
      JSON.stringify(value),
    );
    if (value.name) return String(value.name);
    if (value.value) return String(value.value);
    if (value.label) return String(value.label);
    if (value.code) return String(value.code);
    if (value.text) return String(value.text);
    // ✅ CRITICAL FIX: Return "" so "{}" never renders on screen
    return "";
  }
  return String(value);
};

// ✅ Build safe QR string — all fields coerced before concatenation
const buildQRValue = (data) => {
  return [
    safeString(data.rc_number, "rc_number"),
    safeString(data.vehicle_chasi_number, "vehicle_chasi_number"),
    safeString(data.vehicle_engine_number, "vehicle_engine_number"),
    safeString(data.maker_description, "maker_description"),
    safeString(data.maker_model, "maker_model"),
    safeString(data.body_type, "body_type"),
    safeString(data.fuel_type, "fuel_type"),
    safeString(data.norms_type, "norms_type"),
    safeString(data.manufacturing_date, "manufacturing_date"),
    safeString(data.seat_capacity, "seat_capacity"),
    safeString(data.unladen_weight, "unladen_weight"),
    safeString(data.vehicle_gross_weight, "vehicle_gross_weight"),
    safeString(data.present_address, "present_address"),
    safeString(data.registration_date, "registration_date"),
    safeString(data.color, "color"),
    safeString(data.seat_capacity, "seat_capacity"),
    safeString(data.standing_capacity, "standing_capacity"),
    safeString(data.financer, "financer"),
    safeString(data.insurance_company, "insurance_company"),
    safeString(data.insurance_policy_number, "insurance_policy_number"),
    safeString(data.insurance_upto, "insurance_upto"),
    safeString(data.fit_up_to, "fit_up_to"),
    safeString(data.pucc_number, "pucc_number"),
    safeString(data.pucc_upto, "pucc_upto"),
    safeString(data.rto_code, "rto_code"),
  ].join("");
};

export default function DigiLockerRCView({ data }) {
  if (!data) return null;

  // ✅ Log all incoming data fields for debugging
  console.group(
    `🔍 [DigiLockerRC] Data field type audit for VRN: ${safeString(data?.rc_number, "rc_number")}`,
  );
  Object.entries(data).forEach(([key, val]) => {
    const type = Array.isArray(val) ? "array" : typeof val;
    const isProblematic = type === "object" && val !== null;
    console.log(
      `  ${isProblematic ? "❌" : "✅"} ${key}: [${type}] = ${isProblematic ? JSON.stringify(val) : val}`,
    );
  });
  console.groupEnd();

  const qrValue = buildQRValue(data);

  return (
    <div className="w-full p-8 mx-auto">
      <div className="w-full mx-auto bg-white shadow-md border border-black text-gray-900">
        {/* Header */}
        <div className="text-center p-4 flex items-center justify-center border-black border-b w-full h-auto">
          <div className="absolute left-[4rem]">
            <img
              src={alogo}
              alt="Emblem"
              className="mx-auto w-[4rem] h-[6rem]"
            />
          </div>
          <div className="w-full">
            <h1 className="text-3xl font-bold mt-2">
              Registration Certificate of Vehicle
            </h1>
            <p className="text-gray-700 text-2xl">
              Issuing Authority :{" "}
              {safeString(data.registered_at, "registered_at")}
            </p>
          </div>
        </div>

        {/* Registration Details */}
        <div className="border-b border-black">
          <h2 className="font-bold text-2xl text-center text-gray-800">
            Registration Details
          </h2>
          <table className="w-full text-sm md:text-base border-separate [border-spacing:0.5rem]">
            <tbody className="font-bold text-sm">
              <tr>
                <td>
                  <strong>Registration Number </strong>
                </td>
                <td>
                  :<span className="ml-4"> </span>
                  {safeString(data.rc_number, "rc_number")}
                </td>
                <td>
                  <strong>Registration Date </strong>
                </td>
                <td>
                  : <span className="ml-4"> </span>{" "}
                  {safeString(data.registration_date, "registration_date")}
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Vehicle Class </strong>
                </td>
                <td>
                  : <span className="ml-4"> </span>{" "}
                  {safeString(data.vehicle_category, "vehicle_category")}
                </td>
                <td>
                  <strong>Vehicle Model </strong>
                </td>
                <td>
                  : <span className="ml-4"> </span>{" "}
                  {safeString(data.maker_model, "maker_model")}
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Fuel Type </strong>
                </td>
                <td>
                  : <span className="ml-4"> </span>{" "}
                  {safeString(data.fuel_type, "fuel_type")}
                </td>
                <td>
                  <strong>Vehicle Color </strong>
                </td>
                <td>
                  : <span className="ml-4"> </span>{" "}
                  {safeString(
                    data.vehicle_color || data.color,
                    "vehicle_color",
                  )}
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Chassis Number </strong>
                </td>
                <td>
                  : <span className="ml-4"> </span>{" "}
                  {safeString(
                    data.vehicle_chasi_number,
                    "vehicle_chasi_number",
                  )}
                </td>
                <td>
                  <strong>Engine Number </strong>
                </td>
                <td>
                  {" "}
                  : <span className="ml-4"> </span>{" "}
                  {safeString(
                    data.vehicle_engine_number,
                    "vehicle_engine_number",
                  )}
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Mon/Year of Mfg. </strong>
                </td>
                <td>
                  : <span className="ml-4"> </span>{" "}
                  {safeString(
                    data.mfg_date || data.manufacturing_date,
                    "mfg_date",
                  )}
                </td>
                <td>
                  <strong>Cubic Capacity </strong>
                </td>
                <td>
                  : <span className="ml-4"> </span>{" "}
                  {safeString(data.cubic_capacity, "cubic_capacity")}
                </td>
              </tr>
              <br />
              <tr>
                <td>
                  <strong>Owner Name </strong>
                </td>
                <td>
                  : <span className="ml-4"> </span>{" "}
                  {safeString(data.owner_name, "owner_name")}
                </td>
                <td>
                  <strong>S/W/D Name </strong>
                </td>
                <td>
                  : <span className="ml-4"> </span>{" "}
                  {safeString(data.father_name, "father_name")}
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Present Address </strong>
                </td>
                <td colSpan={3}>
                  : <span className="ml-4"> </span>{" "}
                  {safeString(data.present_address, "present_address")}
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Permanent Address </strong>
                </td>
                <td colSpan={3}>
                  : <span className="ml-4"> </span>{" "}
                  {safeString(data.permanent_address, "permanent_address")}
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Name of Financer </strong>
                </td>
                <td colSpan={3}>
                  : <span className="ml-4"> </span>{" "}
                  {safeString(data.financer, "financer")}
                </td>
              </tr>
              <tr>
                <td>
                  <strong>RC Status </strong>
                </td>
                <td>
                  : <span className="ml-4"> </span>{" "}
                  {safeString(data.rc_status, "rc_status")}
                </td>
                <td>
                  <strong>RC Blacklist Status </strong>
                </td>
                <td>
                  : <span className="ml-4"> </span>{" "}
                  {safeString(data.rc_blacklist_status, "rc_blacklist_status")}
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Tax Up to </strong>
                </td>
                <td>
                  : <span className="ml-4"> </span>{" "}
                  {safeString(data.tax_upto, "tax_upto")}
                </td>
                <td>
                  <strong>Owner Sno. </strong>
                </td>
                <td>
                  : <span className="ml-4"> </span>{" "}
                  {safeString(data.owner_number, "owner_number")}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Insurance Details */}
        <div className="border-b border-black">
          <h2 className="font-bold text-2xl text-center text-gray-800">
            Insurance Details
          </h2>
          <table className="w-full text-sm md:text-base border-separate [border-spacing:0.5rem]">
            <tbody className="font-bold text-sm">
              <tr>
                <td className="w-[19%]">
                  <strong>Insurance Company</strong>
                </td>
                <td colSpan={2}>
                  : <span className="ml-4"></span>
                  {safeString(data.insurance_company, "insurance_company")}
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Policy Number</strong>
                </td>
                <td className="w-[28%]">
                  : <span className="ml-4"></span>
                  {safeString(
                    data.insurance_policy_number,
                    "insurance_policy_number",
                  )}
                </td>
                <td className="w-[19%]">
                  <strong>Valid Upto</strong>
                </td>
                <td>
                  : <span className="ml-4"></span>
                  {safeString(data.insurance_upto, "insurance_upto")}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* PUC Certificate */}
        <div className="border-b border-black">
          <h2 className="font-bold text-2xl text-center text-gray-800">
            PUC Certificate
          </h2>
          <table className="w-full text-sm md:text-base border-separate [border-spacing:0.5rem]">
            <tbody className="font-bold text-sm">
              <tr>
                <td className="w-[19%]">
                  <strong>Certificate Number</strong>
                </td>
                <td className="w-[28%]">
                  :{" "}
                  <span className="ml-4">
                    {safeString(
                      data.pucc_certificate_number,
                      "pucc_certificate_number",
                    ) || " "}
                  </span>
                </td>
                <td className="w-[19%]">
                  <strong>Valid Upto</strong>
                </td>
                <td>
                  :{" "}
                  <span className="ml-4">
                    {safeString(data.pucc_upto, "pucc_upto")}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Fitness Certificate */}
        <div className="border-b-2">
          <h2 className="font-bold text-2xl text-center text-gray-800">
            Fitness Certificate
          </h2>
          <p className="text-center">Form 38 - [See Rule 62(1)]</p>
          <table className="w-1/2 text-sm md:text-base p-4 border-separate [border-spacing:0.5rem]">
            <tbody className="font-bold text-sm">
              <tr>
                <td>
                  <strong>Valid Upto </strong>
                </td>
                <td>
                  : <span className="ml-4"> </span>{" "}
                  {safeString(data.fit_up_to, "fit_up_to")}
                </td>
                <td>
                  <strong> </strong>
                </td>
                <td>
                  <span className="ml-4"> </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Footer with QR */}
        <div className="flex flex-row justify-between items-center p-6 border-black border-t relative">
          <div>
            <QRCodeSVG
              value={qrValue || " "}
              level="H"
              size={150}
              includeMargin={false}
            />
          </div>
          <div>
            <img
              src={digilocker}
              alt="digilocker"
              className="w-60 h-30 ml-12"
            />
          </div>
          <div className="flex flex-row items-end bg-gray-50 p-2 w-[24%] relative">
            <div className="flex flex-col items-start text-[8px]">
              <p>
                Digitally signed by
                <br />
                Ministry of Road <br />
                Transport & Highways{" "}
              </p>
              <p>Government of India </p>
              <p>Date: </p>
            </div>
            <div className="absolute -top-2 right-4">
              <img src={check} alt="check" className="w-8 h-12" />
            </div>
          </div>
        </div>

        {/* Note */}
        <div className="text-xs p-4 border-t border-black tracking-tighter leading-tight">
          <div className="flex items-center mb-2">
            <span className="font-bold">Note</span>
          </div>
          <ol className="list-decimal pl-5 space-y-1">
            <li>
              This is a digital cashflow. The format of this certificate may
              differ from document issued by the concerned department.
            </li>
            <li>
              This certificate is generated by DigiLocker (
              <a
                href="https://digilocker.gov.in"
                className="text-blue-600 hover:underline"
              >
                https://digilocker.gov.in
              </a>
              ) directly from concerned department database.
            </li>
            <li>
              This digitally signed document is legally valid as per the IT Act
              2000 when used electronically.
            </li>
            <li>
              To verify the print out of this certificate, download DigiLocker
              Android application from Google Play store and scan the QR code on
              the printed certificate.
            </li>
          </ol>
        </div>
      </div>
    </div>
  );
}
